const storeKey = '__nahtmaterialAgbAcceptanceStore';
const stateEventName = 'nahtmaterial:agb-acceptance-state';

if (!window[storeKey]) {
  let state = { status: 'idle', value: '' };
  let queue = Promise.resolve();
  let requestSequence = 0;
  let initialized = false;

  const publish = (nextState) => {
    state = nextState;
    document.dispatchEvent(new CustomEvent(stateEventName, { detail: state }));
  };

  const cartUpdateUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.replace(/\/?$/, '/') + 'cart/update.js';
  };

  const write = (value) => {
    const requestId = ++requestSequence;
    publish({ status: 'saving', value });

    const operation = queue
      .catch(() => undefined)
      .then(async () => {
        const response = await fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          keepalive: true,
          body: JSON.stringify({ attributes: { agb_acceptance: value } }),
        });

        if (!response.ok) throw new Error('AGB acceptance could not be stored.');
        return response.json();
      });

    queue = operation;

    return operation.then(
      (cart) => {
        if (requestId === requestSequence) {
          publish({ status: value === '' ? 'idle' : 'accepted', value });
        }
        return cart;
      },
      (error) => {
        if (requestId === requestSequence) {
          publish({ status: 'error', value: '' });
        }
        throw error;
      },
    );
  };

  const initialize = () => {
    if (!initialized) {
      initialized = true;
      return write('');
    }

    return queue;
  };

  const clear = () => {
    if (
      initialized &&
      state.value === '' &&
      (state.status === 'idle' || state.status === 'saving')
    ) {
      return queue;
    }

    initialized = true;
    return write('');
  };

  const storeApi = {
    get state() {
      return state;
    },
    write,
    initialize,
    clear,
  };

  const clearAcceptance = () => {
    void storeApi.clear().catch(() => undefined);
  };

  window[storeKey] = storeApi;

  [
    'line-item:will-change',
    'cart:change',
    'cart:updated',
    'cart:refresh',
    'ajaxProduct:added',
  ].forEach((eventName) => {
    document.addEventListener(eventName, clearAcceptance);
  });

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element
        ? event.target.closest(
            '[is="cart-remove-button"], [data-cart-remove], [name="minus"], [name="plus"], [data-quantity-button]',
          )
        : null;

      if (target) clearAcceptance();
    },
    true,
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.closest('cart-items') &&
        (target.name === 'updates[]' || target.hasAttribute('data-index'))
      ) {
        clearAcceptance();
      }
    },
    true,
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action.includes('/cart')) return;
      if (submitter instanceof HTMLElement && submitter.getAttribute('name') === 'checkout') return;
      clearAcceptance();
    },
    true,
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) clearAcceptance();
  });
}

const acceptanceStore = window[storeKey];

if (!customElements.get('nahtmaterial-agb-checkout-gate')) {
  customElements.define(
    'nahtmaterial-agb-checkout-gate',
    class extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();

        this.checkbox = this.querySelector('[data-agb-acceptance]');
        this.checkoutButton = this.querySelector('[data-agb-checkout]');
        this.errorMessage = this.querySelector('[data-agb-error]');

        if (!this.checkoutButton) return;

        if (!this.checkbox) {
          this.checkoutButton.disabled = true;
          return;
        }

        this.checkbox.addEventListener(
          'change',
          () => {
            const value = this.checkbox.checked ? this.dataset.acceptanceValue : '';
            void acceptanceStore.write(value).catch(() => undefined);
          },
          { signal: this.abortController.signal },
        );

        document.addEventListener(
          stateEventName,
          (event) => this.synchronize(event.detail),
          { signal: this.abortController.signal },
        );

        document.addEventListener(
          'nahtmaterial:tax-id-state',
          () => this.synchronize(acceptanceStore.state),
          { signal: this.abortController.signal },
        );

        const initialization = acceptanceStore.initialize();
        this.synchronize(acceptanceStore.state);
        void initialization.catch(() => undefined);
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      synchronize(nextState) {
        const pending = nextState.status === 'saving';
        const accepted =
          nextState.status === 'accepted' &&
          nextState.value === this.dataset.acceptanceValue;

        this.checkbox.checked =
          nextState.value === this.dataset.acceptanceValue &&
          (pending || accepted);
        this.checkbox.disabled = pending;
        this.checkoutButton.disabled = !accepted || window.__nahtmaterialTaxIdStore?.state.ready !== true;
        this.toggleAttribute('aria-busy', pending);

        if (this.errorMessage) {
          this.errorMessage.hidden = nextState.status !== 'error';
        }
      }
    },
  );
}
